import {
  createEffect,
  createMemo,
  createSignal,
  For,
  Match,
  onMount,
  Show,
  Switch,
} from "solid-js";
import SolidMarkdown from "solid-markdown";
import { defaultCoin, discountIconGreenfill } from "../../assets/assets";
import { sdkEventManager } from "../../data/events";
import { getMarkdown } from "../../data/static";
import {
  goBack,
  goBackNTimes,
  HubbleRoute,
  PageAlignment,
  pushPage,
} from "../../shared_states/modal";
import { classNames } from "../../utils/etc";
import { toIndianNumber, toRupees } from "../../utils/number";
import {
  BackButton,
  ButtonRegular,
  SwitchButton,
  ThreeDotLoader,
} from "../../widgets/button";
import { PhosphorIcon } from "../../widgets/icons";
import { ResourceLoader } from "../../widgets/resource_loader";
import { Spacer } from "../../widgets/spacer";
import { logIntoHubble } from "../hubble_otp_auth/hubble_otp_auth";
import { SummaryModal } from "../summary_modal/summary_modal";
import {
  refreshCart,
  VoucherCart,
  VoucherCartState,
} from "../voucher_edit/voucher";
import { VoucherCard } from "../voucher_edit/voucher_card";
import { openVoucherGen } from "../voucher_edit/voucher_edit";
import {
  PreReviewPopupDetails,
  SimulatedTransactionDetails,
  VoucherProduct,
} from "~/server/types/brand";
import { TotalAndDiscountedAmount } from "~/screens/vocher_l2/voucher_l2_total_and_discount_amount";
import { useClassicContext } from "~/data/classic_context";
import { UseCoinsToFundBaseDiscountStrip } from "~/screens/vocher_l2/voucher_l2_use_coins_to_fund_base_discount";
import { SdkWarningModal } from "../warning_modal/warning_modal";
import { PhoneCollectionModal } from "./phone_collection_modal";
import { CustomCoinInputModal } from "./custom_coin_input_modal";
import {
  isCustomCoinInputEnabled,
  shouldShowCoinDiscountAsPercentage,
  showHubbleBrandingOnVoucherCard,
} from "~/data/partner_config";
import { EditIcon } from "~/assets/svg_icons/edit_icon";
import { fetchV4Home } from "~/data/auth";
import { SummaryTransferModal } from "../bank_transfer/summary_transfer_modal";
import { getCoinLabelCapitalizedPluralized } from "~/utils/common";

export default function VoucherL2(props: {
  productId: string;
  cart?: VoucherCart;
  setPageComponentProps?: (props: VoucherCart) => void;
}) {
  const { data } = useClassicContext();

  createEffect(() => {
    props.setPageComponentProps?.(cart());
  });

  const [cart, setCart] = createSignal<VoucherCart>(
    props.cart || {
      voucherType: "unknown",
      voucherProductId: props.productId,
      amount: 1000,
    }
  );

  return (
    <ResourceLoader
      isFullPage={true}
      fetch={async () => refreshCart(cart(), true, data.isLoggedIn)}
      onAfterFetch={(cart) => {
        setCart(() => cart);
      }}
    >
      {() => (
        <VoucherL2Content
          cart={cart()}
          onCartChange={(cart) => {
            setCart(() => cart);
          }}
        />
      )}
    </ResourceLoader>
  );
}

function BrandName(props: { voucher: VoucherProduct }) {
  return (
    <div class="fixed z-[8] h-[55px] w-full bg-baseTertiaryLight pt-5 text-center">
      <span class="title-appbar inline-block max-h-[3.6em]  max-w-[60%] overflow-hidden overflow-ellipsis text-mediumSemiBold uppercase leading-[1.8em] text-textDark [word-wrap:break-word]">
        {props.voucher.voucherProductMetadata.title}
      </span>
    </div>
  );
}

function VoucherL2Content(props: {
  cart: VoucherCart;
  onCartChange: (cart: VoucherCart) => void;
}) {
  const { data, setClassicContextData, isClientDonationType } =
    useClassicContext();

  const v4Home = data.isLoggedIn ? data.v4HomePage : null;
  const clientConfig = data.isLoggedIn ? data.clientConfig : null;

  const primaryTransaction = () => props.cart.dryRunTransaction![0];
  const [isCartRefreshing, setIsCartRefreshing] = createSignal(false);

  const isCoinsOnly = props.cart.isCoinsOnly == true;
  const coinIconUrl =
    data.isLoggedIn && data.clientConfig.coinIconUrl
      ? data.clientConfig.coinIconUrl
      : defaultCoin;

  const availableCoins = v4Home?.coins.totalAvailable ?? 0;
  const useCoinsToFundBaseDiscount = data.isLoggedIn
    ? data.clientConfig.useCoinsToFundBaseDiscount
    : false;

  const canProceed = createMemo(() => {
    if (isCoinsOnly) {
      return primaryTransaction().requiredDeposit == 0;
    }

    if (useCoinsToFundBaseDiscount)
      return availableCoins >= primaryTransaction().totalRequiredCoins;

    return true;
  });

  let partnerCustomCoinInputEnabled = false;
  if (data.isLoggedIn == true && data.mode == "sdk") {
    partnerCustomCoinInputEnabled = isCustomCoinInputEnabled(
      data.initParams.clientId
    );
  }

  return (
    <div
      class={`no-scrollbar absolute bottom-0 left-0 right-0 top-0 flex flex-col items-stretch overflow-x-hidden overflow-y-scroll bg-baseTertiaryLight bg-[linear-gradient(_98deg,_#00af5412_3.51%,_#ffe97226_92.74%_)]`}
    >
      <div class=" flex flex-col items-stretch rounded-b-2xl bg-baseTertiaryLight pb-5">
        <BackButton />

        <BrandName voucher={props.cart.voucherProduct!} />
        <div class="h-[60px]"></div>

        <VoucherCard
          class="mx-5 my-0 w-[calc(100%-40px)] self-center max-[375px]:self-stretch"
          product={props.cart.voucherProduct!}
          value={props.cart}
          onEdit={() => {
            let cart = props.cart;
            cart.coinsToConsume = undefined;
            return openVoucherGen({
              onContinue: (cart) => {
                goBack(() => {
                  if (props.onCartChange) {
                    props.onCartChange(cart);
                  }
                });
              },
              productId: cart.voucherProductId,
              currentCart: cart,
              voucherProduct: cart.voucherProduct,
            });
          }}
          showEditButton={true}
          showHubbleBranding={showHubbleBrandingOnVoucherCard(
            data.isLoggedIn && data.mode == "sdk"
              ? data.initParams.clientId
              : undefined
          )}
        />

        <Show when={!clientConfig?.payoutsEnabled}>
          <TotalAndDiscountedAmount
            isCoinsOnly={isCoinsOnly}
            coinIconUrl={coinIconUrl}
            primaryTransaction={primaryTransaction}
          />
        </Show>

        <Show when={clientConfig?.payoutsEnabled}>
          <BankTransferDetailsCard
            cart={props.cart}
            primaryTransaction={primaryTransaction}
          />
        </Show>

        <Switch>
          <Match when={props.cart.couponCode}>
            <AppliedCouponCard
              cart={props.cart}
              onCartChange={props.onCartChange}
            />
          </Match>

          <Match when={true}>
            <ApplyCouponButton
              cart={props.cart}
              onCartChange={props.onCartChange}
              isLoggedIn={data.isLoggedIn}
              isClientDonationType={isClientDonationType}
            />
          </Match>
        </Switch>

        <Switch>
          <Match
            when={
              data.isLoggedIn && data.clientConfig.useCoinsToFundBaseDiscount
            }
          >
            <UseCoinsToFundBaseDiscountStrip
              requiredCoins={() => primaryTransaction().totalRequiredCoins}
              discountAmount={() =>
                primaryTransaction().discountDetails.hubbleBaseDiscount.amount
              }
            />
          </Match>
          <Match
            when={
              !isCoinsOnly &&
              data.isLoggedIn &&
              v4Home?.coins?.totalAvailable &&
              v4Home?.coins?.totalAvailable > 0 &&
              data.clientConfig?.coinsEnabled
            }
          >
            <UseCoinsCard
              cart={props.cart}
              partnerCustomCoinInputEnabled={partnerCustomCoinInputEnabled}
              isInProgress={isCartRefreshing()}
              useCoins={props.cart.useCoins == true}
              coinIconUrl={coinIconUrl}
              onChange={async (useCoins, coinsToConsume) => {
                setIsCartRefreshing(true);
                const cartToRefresh: VoucherCart = {
                  ...props.cart,
                  useCoins: useCoins,
                };
                if (coinsToConsume !== undefined) {
                  cartToRefresh.coinsToConsume = coinsToConsume;
                } else {
                  if (!useCoins) {
                    cartToRefresh.coinsToConsume = undefined;
                  }
                }

                const cart = await refreshCart(
                  cartToRefresh,
                  false,
                  data.isLoggedIn
                );
                setIsCartRefreshing(false);
                props.onCartChange(cart);
              }}
            />
          </Match>
        </Switch>

        <Show when={primaryTransaction().requiredDeposit > 0 && isCoinsOnly}>
          <div class="mx-5 my-0 mb-3 mt-1.5 box-border flex items-center justify-between self-stretch rounded-xl border border-[#bb2d13] bg-[linear-gradient(_90deg,_#bb2d131f_2.8%,_#bb2d1300_82.89%_)] p-4">
            <span class="flex items-center gap-1 text-f12Bold text-errorDark">
              <PhosphorIcon name="warning" class="text-[20px] text-errorDark" />
              Insufficient rewards
            </span>
            <span class="flex items-center gap-1 text-f12Bold text-textDark">
              You have: <img src={coinIconUrl} class="w-h-4 h-4" />{" "}
              {toIndianNumber(v4Home!.coins.totalAvailable)}
            </span>
          </div>
        </Show>
        <ButtonRegular
          onClick={onClickGetThisCard}
          isEnabled={canProceed()}
          class={"button-filled mx-5 my-0 mt-1.5"}
        >
          <Switch>
            <Match when={isClientDonationType()}>
              <Show
                when={
                  primaryTransaction().discountDetails.couponDiscount.amount
                }
                fallback={
                  <span>Pay {toRupees(primaryTransaction().dueAmount)}</span>
                }
              >
                <span>
                  Pay {toRupees(primaryTransaction().dueAmount)}{" "}
                  <span class="text-medium text-[#7c7c7c] line-through">
                    {toRupees(primaryTransaction().redemptionAmount)}
                  </span>{" "}
                </span>
              </Show>
            </Match>
            <Match when={true}>Continue</Match>
          </Switch>
        </ButtonRegular>
      </div>

      <div class="box-border flex flex-col items-stretch gap-4 self-stretch p-4">
        <Switch>
          <Match
            when={
              props.cart.voucherProduct!.voucherProductMetadata.howToUseSteps
            }
          >
            <HowItWorksSteps voucher={props.cart.voucherProduct!} />
          </Match>
          <Match when={true}>
            <HowToUseEntryCard
              onClick={() =>
                pushPage(
                  () => <HowToUse voucher={props.cart.voucherProduct!} />,
                  { isModal: true },
                  HubbleRoute.HOW_TO_USE
                )
              }
            />
          </Match>
        </Switch>

        <TermsAndConditions voucher={props.cart.voucherProduct!} />
      </div>
    </div>
  );

  function BankTransferDetailsCard(props: {
    primaryTransaction: () => SimulatedTransactionDetails;
    cart: VoucherCart;
  }) {
    return (
      <div class="mx-5 my-4 flex h-[58px] items-center justify-between gap-2 rounded-xl bg-baseTertiaryMedium px-4 py-3">
        <img src={defaultCoin} class="h-[25px] w-[25px]" alt="coin" />
        <div class="w-[154px]">
          <p class="text-mediumSemiBold text-textDark">
            Using{" "}
            <span class="text-coinPercentageOrange">
              {toIndianNumber(props.primaryTransaction().totalRequiredCoins)}
            </span>{" "}
            incentive
          </p>
          <p class="text-small">
            Balance: {toRupees(v4Home?.coins.totalAvailable ?? 0)}
          </p>
        </div>
        <p
          onClick={() => {
            pushPage(
              () => (
                <SummaryTransferModal
                  type={"voucher"}
                  cart={props.cart}
                  amount={
                    props.cart.dryRunTransaction?.[0].totalRequiredCoins ??
                    props.primaryTransaction().totalRequiredCoins ??
                    0
                  }
                  taxAmount={
                    props.cart.dryRunTransaction?.[0]?.additionalCharges
                      .tdsCharges.amount ?? 0
                  }
                  redeemAmount={
                    props.cart.dryRunTransaction?.[0].totalRedeemableCoins ??
                    props.primaryTransaction().totalRedeemableCoins ??
                    0
                  }
                  discountDetails={() =>
                    props.cart.dryRunTransaction?.[0].discountDetails
                  }
                  dryRunTransaction={() => props.cart.dryRunTransaction?.[0]!}
                  totalRequiredCoins={() =>
                    props.cart.dryRunTransaction?.[0].totalRequiredCoins ?? 0
                  }
                />
              ),
              {
                isModal: true,
                alignment: PageAlignment.bottom,
                offset: { top: 20, right: 20 },
              },
              HubbleRoute.BANK_SUMMARY_TRANSFER
            );
          }}
          class="items-baseline self-end text-[10px] font-semibold uppercase tracking-[-0.1px] text-textDark underline [text-underline-position:from-font]"
        >
          192(R) TAX adjusted
        </p>
      </div>
    );
  }

  function onClickGetThisCard() {
    const preReviewPopupDetails =
      props.cart.voucherProduct?.voucherProductMetadata?.preReviewPopupDetails;

    if (data.isLoggedIn) {
      if (!data.v4HomePage.user.phoneNumber) {
        pushPage(
          () => (
            <PhoneCollectionModal
              onPhoneVerified={async () => {
                await goBackNTimes(2);
                onAfterLogin();
                const v4HomePage = await fetchV4Home();
                // @ts-ignore
                setClassicContextData("v4HomePage", (_) => v4HomePage);
              }}
            />
          ),
          {
            isModal: false,
          },
          HubbleRoute.PHONE_COLLECTION
        );
      } else if (clientConfig?.payoutsEnabled) {
        pushPage(
          () => (
            <SummaryTransferModal
              type={"voucher"}
              cart={props.cart}
              amount={props.cart.dryRunTransaction?.[0].totalRequiredCoins ?? 0}
              taxAmount={
                props.cart.dryRunTransaction?.[0]?.additionalCharges.tdsCharges
                  .amount ?? 0
              }
              redeemAmount={
                props.cart.dryRunTransaction?.[0].totalRedeemableCoins ?? 0
              }
              isGetThisCard={true}
            />
          ),
          {
            isModal: true,
            alignment: PageAlignment.bottom,
            offset: { top: 20, right: 20 },
          },
          HubbleRoute.BANK_SUMMARY_TRANSFER
        );
      } else {
        onAfterLogin();
      }
    } else {
      logIntoHubble(() => {
        onAfterLogin();
      }, sdkEventManager);
    }

    function onAfterLogin() {
      if (preReviewPopupDetails != null) {
        openWarningModal({
          onContinue: async () => {
            goBack();
            await new Promise((resolve) => setTimeout(resolve, 300));
            openSummaryModal();
          },
          preReviewPopupDetails: preReviewPopupDetails,
        });
      } else {
        openSummaryModal();
      }
    }

    function openSummaryModal() {
      pushPage(
        () => <SummaryModal cart={props.cart} />,
        {
          isModal: true,
          eventProps: {
            brandName: props.cart.voucherProduct?.name,
            amount: props.cart.amount,
            couponCode: props.cart.couponCode,
            useCoins: props.cart.useCoins,
          },
        },
        HubbleRoute.CHECKOUT_SUMMARY
      );
    }

    function openWarningModal(props: {
      onContinue: () => void;
      preReviewPopupDetails: PreReviewPopupDetails;
    }) {
      pushPage(
        () => {
          return <SdkWarningModal {...props} />;
        },
        {
          pushState: true,
          isModal: true,
          disableCloseButton: true,
        },

        HubbleRoute.WARNING_MODAL
      );
    }
  }
}

function AppliedCouponCard(props: {
  cart: VoucherCart;
  onCartChange: (cart: VoucherCart) => void;
}) {
  const { data } = useClassicContext();
  const [isRefreshing, setIsRefreshing] = createSignal(false);

  return (
    <div class="z-[1] mx-5 my-0 mb-3 box-border flex flex-row gap-1 rounded-xl border border-baseTertiaryDark bg-baseTertiaryLight p-4 [box-shadow:0px_4px_6px_0px_#dbddde33]">
      <CouponIcon />
      <span class="flex-[1] text-bold text-successDark">
        {props.cart.couponCode} applied
      </span>
      <Show
        when={isRefreshing()}
        fallback={
          <span
            class="text-bold text-baseTertiaryMedium"
            onClick={async () => {
              setIsRefreshing(true);
              const cart = await refreshCart(
                {
                  ...props.cart,
                  couponCode: undefined,
                },
                false,
                data.isLoggedIn
              );
              setIsRefreshing(false);
              props.onCartChange(cart);
            }}
          >
            Remove
          </span>
        }
      >
        <ThreeDotLoader />
      </Show>
    </div>
  );
}

function ApplyCouponButton(props: {
  cart: VoucherCart;
  onCartChange: (cart: VoucherCart) => void;
  isLoggedIn: boolean;
  isClientDonationType: () => boolean;
}) {
  return (
    <div
      class="z-[1] mx-5 mb-5 mt-0 box-border flex items-center justify-between gap-1.5 self-stretch rounded-xl border border-baseTertiaryDark bg-baseTertiaryLight p-4"
      onClick={() => {
        if (props.isLoggedIn) {
          pushPage(
            () => (
              <CouponListing
                cart={props.cart}
                onCartChange={(cart) => {
                  goBack(() => {
                    props.onCartChange(cart);
                  });
                }}
              />
            ),
            undefined,
            HubbleRoute.COUPONS
          );
        } else {
          logIntoHubble(() => {
            pushPage(
              () => (
                <CouponListing
                  cart={props.cart}
                  onCartChange={(cart) => {
                    goBack(() => {
                      props.onCartChange(cart);
                    });
                  }}
                />
              ),
              undefined,
              HubbleRoute.COUPONS
            );
          }, sdkEventManager);
        }
      }}
    >
      <CouponIcon class="pr-1.5 [fill:#000]" />
      <span class="title-secondary flex-1 text-mediumSemiBold text-textDark">
        <Switch>
          <Match when={props.isClientDonationType()}>
            Apply coupon for discount
          </Match>
          <Match when={true}>Apply coupon code</Match>
        </Switch>
      </span>
      <PhosphorIcon
        label="Apply coupon for discount"
        name="caret-right"
        size="bold"
        class=""
      />
    </div>
  );
}

function HowToUse(props: { voucher: VoucherProduct }) {
  return (
    <div class="bottom-0 left-0 right-0 top-0 flex overflow-hidden">
      <img
        class="w-full object-cover"
        src={props.voucher.voucherProductMetadata.howItWorksVideoUrl}
      />
    </div>
  );
}

function CouponListing(props: {
  cart: VoucherCart;
  onCartChange: (cart: VoucherCart) => void;
}) {
  const { data } = useClassicContext();

  return (
    <ResourceLoader
      fetch={async () => {
        return refreshCart(props.cart, true, data.isLoggedIn);
      }}
    >
      {(cart) => (
        <CouponListingContent cart={cart} onCartChange={props.onCartChange} />
      )}
    </ResourceLoader>
  );
}
function CouponListingContent(props: {
  cart: VoucherCart;
  onCartChange: (cart: VoucherCart) => void;
}) {
  const { data } = useClassicContext();
  const [couponCode, setCouponCode] = createSignal("");
  const [isCartRefreshing, setIsCartRefreshing] = createSignal(false);
  const [error, setError] = createSignal<string | undefined>(undefined);

  async function applyCoupon() {
    setIsCartRefreshing(true);
    const cart = await refreshCart(
      {
        ...props.cart,
        couponCode: couponCode(),
      },
      false,
      data.isLoggedIn
    );
    setIsCartRefreshing(false);

    if (cart.cartState == VoucherCartState.valid) {
      setError(undefined);
      props.onCartChange(cart);
    } else {
      setError(cart.cartMessage);
    }
  }

  return (
    <div class="absolute bottom-0 left-0 right-0 top-0 box-border flex flex-col items-stretch bg-baseTertiaryLight p-[20px] pt-[66px]">
      <BackButton />
      <div class="flex flex-row items-stretch">
        <input
          class="flex flex-[1] flex-col items-start gap-3 rounded-bl-xl rounded-br-none rounded-tl-xl rounded-tr-none border border-r-0 border-baseTertiaryDark bg-baseTertiaryLight px-4 py-[13px] text-[16px] font-medium outline-none [box-shadow:0px_3px_8px_0px_#0000000a]"
          value={couponCode()}
          autofocus={!props.cart.coupons || props.cart.coupons.length === 0}
          placeholder="Enter coupon code"
          onInput={(event) => {
            setCouponCode(event.target.value);
          }}
        />
        <ButtonRegular
          class="box-border flex min-w-[70px] items-center rounded-bl-none rounded-br-xl rounded-tl-none rounded-tr-xl border border-baseTertiaryDark bg-[#212427] px-2.5 py-0 text-[#fff]"
          onClick={async () => {
            applyCoupon();
          }}
          isEnabled={true}
          isLoading={isCartRefreshing()}
        >
          Apply
        </ButtonRegular>
      </div>
      <Show when={error()}>
        <span class="px-0 py-2 text-medium text-errorDark">{error()}</span>
      </Show>
      <Show when={props.cart.coupons && props.cart.coupons.length > 0}>
        <Spacer height={24} />
      </Show>
      <For each={props.cart.coupons ?? []}>
        {(coupon) => (
          <div
            class="inline-flex h-16 w-full items-start justify-start gap-2 overflow-hidden rounded-2xl border border-[#dddddd] bg-white py-3 pl-3 pr-4"
            onClick={async () => {
              if (isCartRefreshing() || !coupon.applicability.isApplicable) {
                return;
              }

              setCouponCode(coupon.code);
              await applyCoupon();
            }}
          >
            <div class="flex h-10 w-10 items-center justify-center gap-2.5 rounded-lg bg-[#e7f3ec]">
              <div class="relative h-6 w-6">
                <img
                  class="absolute left-[-0px] top-[-0px] h-6 w-6"
                  src={discountIconGreenfill}
                ></img>
              </div>
            </div>
            <div class="inline-flex shrink grow basis-0 flex-col items-start justify-center gap-0.5">
              <div class="text-bold text-[#0f8849]">
                {coupon.metadata.page.title}
              </div>
              <div class="text-f12 text-[#7c7c7c]">
                {coupon.applicability.isApplicable
                  ? coupon.metadata.page.subtitle
                  : coupon.applicability.nonApplicabilityReason}
              </div>
            </div>
            <div
              class="text-mediumBold underline"
              classList={{
                "text-[#212427]": coupon.applicability.isApplicable,
                "text-[#ccc]": !coupon.applicability.isApplicable,
              }}
            >
              Apply
            </div>
          </div>
        )}
      </For>
    </div>
  );
}

function HowItWorksSteps(props: { voucher: VoucherProduct }) {
  const [steps, setSteps] = createSignal(
    props.voucher.voucherProductMetadata.howToUseSteps[0]
  );

  return (
    <div class="no-border primary-box-shadow box-border flex flex-col gap-2.5 rounded-[20px] border border-baseTertiaryDark bg-baseTertiaryLight p-4 [box-shadow:0px_3px_8px_0px_#0000000a]">
      <span class="title-primary text-mediumSemiBold text-basePrimaryDark">
        How to use?
      </span>
      <div class="mb-1 flex flex-row gap-4 border-b border-baseTertiaryMedium text-[11px] font-bold uppercase text-baseSecondaryMedium ">
        <For each={props.voucher.voucherProductMetadata.howToUseSteps}>
          {(step) => (
            <div
              class={classNames(
                "cursor-pointer pb-1",
                steps().retailModeName == step.retailModeName
                  ? "border-b-2 border-basePrimaryDark text-basePrimaryDark"
                  : ""
              )}
              onClick={() => setSteps(step)}
            >
              {step.retailModeName}
            </div>
          )}
        </For>
      </div>

      <For each={steps().howToUseHints.hints}>
        {(step, index) => (
          <div class="flex flex-row gap-2 text-medium text-textDark">
            <span class="flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-[18px]">
              {index() + 1}
            </span>
            <span class="body-small">{step}</span>
          </div>
        )}
      </For>
    </div>
  );
}

function TermsAndConditions(props: { voucher: VoucherProduct }) {
  return (
    <div class="no-border primary-box-shadow box-border flex flex-col gap-2.5 rounded-[20px] border border-baseTertiaryDark bg-baseTertiaryLight p-4 [box-shadow:0px_3px_8px_0px_#0000000a]">
      <span class="title-primary text-mediumSemiBold text-basePrimaryDark">
        Terms and conditions
      </span>
      <For each={props.voucher.voucherProductMetadata.conditions}>
        {(step, index) => (
          <div class="flex flex-row gap-2 text-medium text-textDark">
            <span class="flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-[18px]">
              {index() + 1}
            </span>
            <span class="body-small">{step}</span>
          </div>
        )}
      </For>
      <span
        class="button-secondary flex items-center gap-2 border-t border-baseTertiaryMedium pt-2 text-medium"
        onClick={() => {
          pushPage(
            () => {
              return (
                <FullTnc
                  resId={props.voucher.voucherProductMetadata.tncResId!}
                />
              );
            },
            { isModal: true },
            HubbleRoute.TNC
          );
        }}
      >
        Show all <PhosphorIcon name="arrow-circle-right" size="fill" />
      </span>
    </div>
  );
}

function FullTnc(props: { resId: string }) {
  return (
    <ResourceLoader
      fetch={async () => {
        return getMarkdown(props.resId);
      }}
    >
      {(markdown) => (
        <div class="bottom-0 left-0 right-0 top-0 flex overflow-y-scroll bg-baseTertiaryLight p-5 pt-10">
          <div>
            <span class="title-primary pb-5 text-[28px] font-semibold leading-normal tracking-[-0.64px] text-basePrimaryDark">
              Terms and conditions
            </span>
            <Spacer height={20} />
            <SolidMarkdown class="body-small" children={markdown} />
            <Spacer height={20} />
          </div>
        </div>
      )}
    </ResourceLoader>
  );
}

function UseCoinsCard(props: {
  useCoins: boolean;
  isInProgress: boolean;
  coinIconUrl: string;
  onChange: (value: boolean, coinsToConsume?: number) => void;
  cart: VoucherCart;
  partnerCustomCoinInputEnabled: boolean;
}) {
  const { data } = useClassicContext();
  const isSDK = data.isLoggedIn && data.mode == "sdk";
  const clientConfig = data.isLoggedIn ? data.clientConfig : null;

  const v4Home = data.isLoggedIn ? data.v4HomePage : null;

  const primaryTransaction = () => props.cart.dryRunTransaction![0];

  const [coinDiscount, setCoinDiscount] = createSignal(
    primaryTransaction().discountDetails.coinDiscount
  );
  const [usableCoinsPerc, setUsableCoinsPerc] = createSignal(0);

  // Get clientId for config logic
  let clientId: string | undefined = undefined;
  if (isSDK && data.initParams) {
    clientId = data.initParams.clientId;
  }

  createEffect(() => {
    let coinsDiscountPossible =
      customCoinsToConsume() * coinToInrConversionRatio;
    let cardAmount = primaryTransaction().redemptionAmount;
    setUsableCoinsPerc((coinsDiscountPossible / cardAmount) * 100);
  });

  const defaultUseCoins = () => {
    return (
      data.isLoggedIn &&
      data.mode === "sdk" &&
      data.localClientConfig.consumeCoinsByDefault
    );
  };

  onMount(() => {
    if (defaultUseCoins()) {
      props.onChange(true, customCoinsToConsume());
    }
  });

  let maxCoinCapped = () => {
    if (!primaryTransaction()) {
      return false;
    }
    return (
      primaryTransaction()!.totalRedeemableCoins <
      primaryTransaction()!.totalRequiredCoins
    );
  };

  const coinToInrConversionRatio = clientConfig?.coinToInrConversionRatio ?? 1;

  function customCoinsToConsume() {
    return props.cart.coinsToConsume == undefined
      ? primaryTransaction()!.totalRedeemableCoins
      : props.cart.coinsToConsume;
  }

  if (isSDK) {
    createEffect(() => {
      setCoinDiscount(primaryTransaction().discountDetails.coinDiscount);
    });
  }

  // Helper to get subtext for coin discount
  function getCoinDiscountSubtext() {
    if (shouldShowCoinDiscountAsPercentage(clientId)) {
      if (props.useCoins) {
        return `+${toIndianNumber(usableCoinsPerc())}% off`;
      } else {
        return `Get +${toIndianNumber(usableCoinsPerc())}% off`;
      }
    } else {
      const amount =
        primaryTransaction().discountDetails.coinDiscount.amount > 0
          ? primaryTransaction().discountDetails.coinDiscount.amount
          : customCoinsToConsume() * coinToInrConversionRatio;
      if (props.useCoins) {
        return `+${toRupees(amount)} off`;
      } else {
        return `Get +${toRupees(amount)} off`;
      }
    }
  }

  return (
    <div
      class={classNames(
        "my-0 mb-5 ml-5 mr-5 box-border flex flex-row items-center gap-3 self-stretch rounded-xl border border-baseTertiaryDark bg-baseTertiaryLight stroke-[#212427] px-4 py-[7px] transition-colors duration-300 [transition:background-color_250ms]",
        props.useCoins ? "bg-[#0F88491A]" : ""
      )}
      onClick={() => {
        if (props.isInProgress) {
          return;
        }
        if (props.partnerCustomCoinInputEnabled) {
          pushPage(
            () => (
              <CustomCoinInputModal
                maxCoins={primaryTransaction().totalRedeemableCoins}
                currentCoins={customCoinsToConsume()}
                coinIconUrl={props.coinIconUrl}
                coinToInrConversionRatio={coinToInrConversionRatio}
                giftCardValue={primaryTransaction().redemptionAmount}
                onApply={(coins) => {
                  goBack(() => {
                    props.onChange(coins > 0, coins);
                  });
                }}
              />
            ),
            { isModal: true },
            HubbleRoute.CUSTOM_COIN_INPUT_MODAL
          );
        } else {
          props.onChange(!props.useCoins, customCoinsToConsume());
        }
      }}
    >
      <img src={props.coinIconUrl} class="h-5 w-5" />
      <Switch>
        <Match when={isSDK && !props.useCoins}>
          <div class="flex flex-[1] flex-col">
            <span class="flex items-center gap-1 text-mediumBold text-basePrimaryDark">
              Use {toIndianNumber(customCoinsToConsume())}{" "}
              {getCoinLabelCapitalizedPluralized(clientId ?? "")}
              {props.partnerCustomCoinInputEnabled && <EditIcon size="16" />}
            </span>
            <Show
              when={maxCoinCapped()}
              fallback={
                <span class="text-f12 text-basePrimaryDark">
                  {getCoinDiscountSubtext()} • Balance:{" "}
                  {toIndianNumber(v4Home?.coins.totalAvailable ?? 0)}
                </span>
              }
            >
              <span class="text-f12 text-basePrimaryDark">
                {getCoinDiscountSubtext()} • Balance:{" "}
                {toIndianNumber(v4Home?.coins.totalAvailable ?? 0)}
              </span>
            </Show>
          </div>
        </Match>

        <Match when={isSDK && props.useCoins}>
          <div class="flex flex-[1] flex-col">
            <span class="flex items-center gap-1 text-mediumBold text-basePrimaryDark">
              {"Using " +
                toIndianNumber(customCoinsToConsume()) +
                " " +
                getCoinLabelCapitalizedPluralized(clientId ?? "")}
              {props.partnerCustomCoinInputEnabled && <EditIcon size="16" />}
            </span>
            <span class="text-f12 text-successDark">
              {getCoinDiscountSubtext()}
            </span>
          </div>
        </Match>

        <Match when={true}>
          <div class="flex flex-[1] flex-col">
            <span class="text-mediumBold text-textDark">Use coins</span>
            <span class="text-small text-textDark">
              Balance:{" "}
              {data.isLoggedIn && v4Home && v4Home.coins.totalAvailable}
            </span>
          </div>
        </Match>
      </Switch>

      <SwitchButton
        value={props.useCoins}
        onChange={(val) => props.onChange(val, customCoinsToConsume())}
      />
    </div>
  );
}

function HowToUseEntryCard(props: { onClick: () => void }) {
  return (
    <div
      class="box-border flex flex-row items-center gap-4 self-stretch rounded-2xl border border-baseTertiaryDark bg-baseTertiaryLight stroke-[#212427] p-4 [box-shadow:0px_4px_6px_0px_#dbddde33]"
      onClick={props.onClick}
    >
      <HelpIcon />
      <span class="flex-[1] text-bold text-textDark">How to use?</span>
      <PhosphorIcon name="caret-right" size="bold" class="" />
    </div>
  );
}

function HelpIcon() {
  return (
    <svg
      class="text-[24px]"
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path d="M10.0002 15V15.0084M6.66683 8.33337C6.66683 6.49242 8.15921 5.00004 10.0002 5.00004C11.8411 5.00004 13.3335 6.49242 13.3335 8.33337C13.3335 9.88961 12.267 11.1968 10.8251 11.5639C10.3791 11.6774 10.0002 12.0398 10.0002 12.5M19.1668 10C19.1668 15.0626 15.0628 19.1667 10.0002 19.1667C4.93755 19.1667 0.833496 15.0626 0.833496 10C0.833496 4.93743 4.93755 0.833374 10.0002 0.833374C15.0628 0.833374 19.1668 4.93743 19.1668 10ZM10.1067 8.64281C10.4333 8.32886 10.4436 7.80957 10.1297 7.48294C9.81574 7.1563 9.29645 7.14602 8.96981 7.45996C8.64318 7.7739 8.63289 8.2932 8.94684 8.61983C9.26078 8.94646 9.78007 8.95675 10.1067 8.64281ZM15.1481 7.96913C15.4621 8.29576 15.4518 8.81506 15.1251 9.129C14.7985 9.44294 14.2792 9.43266 13.9653 9.10602C13.6513 8.77939 13.6616 8.2601 13.9883 7.94615C14.3149 7.63221 14.8342 7.6425 15.1481 7.96913Z" />
    </svg>
  );
}

function CouponIcon(props: { class?: string; pathClass?: string }) {
  return (
    <svg
      class={`text-[24px] ${props.class}`}
      xmlns="http://www.w3.org/2000/svg"
      width="25"
      height="25"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        class={props.pathClass}
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M2.73789 9.65715L1.3579 10.9835C0.922388 11.4021 0.908673 12.0945 1.32726 12.53L3.62493 14.9206C3.67235 14.8438 3.73012 14.7717 3.79819 14.7062C4.2337 14.2876 4.92609 14.3014 5.34468 14.7369C5.76327 15.1724 5.74956 15.8648 5.31404 16.2834C5.24597 16.3488 5.17162 16.4037 5.09303 16.448L7.39069 18.8385C7.80928 19.2741 8.50167 19.2878 8.93718 18.8692L10.3172 17.5428C9.89858 17.1073 9.91229 16.4149 10.3478 15.9963C10.7833 15.5777 11.4757 15.5914 11.8943 16.0269L19.1885 9.01611C19.624 8.59752 19.6378 7.90513 19.2192 7.46962L13.1557 1.1611C12.7371 0.725587 12.0448 0.71187 11.6092 1.13046L4.31502 8.1413C4.73361 8.57681 4.71989 9.2692 4.28438 9.68779C3.84887 10.1064 3.15648 10.0927 2.73789 9.65715ZM4.83751 11.0525C4.62821 10.8347 4.63507 10.4885 4.85283 10.2792C5.07058 10.0699 5.41678 10.0768 5.62607 10.2945L5.76388 10.4379C5.97317 10.6557 5.96632 11.0019 5.74856 11.2112C5.5308 11.4204 5.18461 11.4136 4.97531 11.1958L4.83751 11.0525ZM6.18109 12.4504C5.9718 12.2326 5.97865 11.8864 6.19641 11.6771C6.41417 11.4678 6.76036 11.4747 6.96966 11.6924L7.10746 11.8358C7.31676 12.0536 7.3099 12.3997 7.09214 12.609C6.87439 12.8183 6.52819 12.8115 6.3189 12.5937L6.18109 12.4504ZM7.53999 13.075C7.32224 13.2843 7.31538 13.6305 7.52467 13.8482L7.66248 13.9916C7.87178 14.2094 8.21797 14.2162 8.43573 14.0069C8.65348 13.7976 8.66034 13.4514 8.45104 13.2337L8.31324 13.0903C8.10394 12.8726 7.75775 12.8657 7.53999 13.075ZM8.86826 15.2461C8.65896 15.0284 8.66582 14.6822 8.88358 14.4729C9.10133 14.2636 9.44753 14.2705 9.65682 14.4882L9.79463 14.6316C10.0039 14.8493 9.99707 15.1955 9.77931 15.4048C9.56155 15.6141 9.21536 15.6073 9.00606 15.3895L8.86826 15.2461ZM11.8861 4.8447C12.1878 4.83058 12.4438 5.06371 12.4579 5.36541L12.7296 11.1724C12.7437 11.4741 12.5106 11.7301 12.2089 11.7443C11.9072 11.7584 11.6512 11.5252 11.6371 11.2235L11.3653 5.41654C11.3512 5.11484 11.5844 4.85882 11.8861 4.8447ZM10.1067 8.64281C10.4333 8.32886 10.4436 7.80957 10.1297 7.48294C9.81574 7.1563 9.29645 7.14602 8.96981 7.45996C8.64318 7.7739 8.63289 8.2932 8.94684 8.61983C9.26078 8.94646 9.78007 8.95675 10.1067 8.64281ZM15.1481 7.96913C15.4621 8.29576 15.4518 8.81506 15.1251 9.129C14.7985 9.44294 14.2792 9.43266 13.9653 9.10602C13.6513 8.77939 13.6616 8.2601 13.9883 7.94615C14.3149 7.63221 14.8342 7.6425 15.1481 7.96913Z"
      />
    </svg>
  );
}
